import React, { useState, useEffect } from "react";
import PageSwitcher from "./components/PageSwitcher";
import ResumePainPage from "./components/ResumePainPage";
import InterviewFearPage from "./components/InterviewFearPage";
import ExperienceGapPage from "./components/ExperienceGapPage";
import CareerConfusionPage from "./components/CareerConfusionPage";
import SubmissionsDashboard from "./components/SubmissionsDashboard";
import { LeadCaptureModal } from "./components/LeadCaptureModal";
import { Toaster } from "sonner";
import { Linkedin, Instagram, Youtube, HelpCircle, ArrowUpRight } from "lucide-react";

export default function App() {
  const [activePage, setActivePage] = useState("resume-pain");
  const [totalLeads, setTotalLeads] = useState(0);

  // Lead Modal configuration
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    checkoutUrl: "",
    buttonText: "",
    defaultPain: ""
  });

  useEffect(() => {
    // Synchronize current active page tab from hash if any
    const handleHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (["resume-pain", "interview-fear", "experience-gap", "career-confusion", "admin-leads"].includes(hash)) {
        setActivePage(hash);
      }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);

    const syncLeadsCount = () => {
      const storedLeads = JSON.parse(localStorage.getItem("vph_captured_leads") || "[]");
      setTotalLeads(storedLeads.length);
    };

    // Initial count sync
    syncLeadsCount();

    // Listen to storage event (across tabs) & custom event (same tab)
    window.addEventListener("storage", syncLeadsCount);
    window.addEventListener("vph_lead_submitted", syncLeadsCount);

    return () => {
      window.removeEventListener("hashchange", handleHash);
      window.removeEventListener("storage", syncLeadsCount);
      window.removeEventListener("vph_lead_submitted", syncLeadsCount);
    };
  }, []);

  const changePage = (pageId) => {
    window.location.hash = pageId;
    setActivePage(pageId);
    
    // Update canonical tag dynamically
    const canon = document.getElementById("meta-canonical");
    if (canon) {
      canon.href = `https://learn.vedulaconsulting.com/${pageId}`;
    }
  };

  const triggerModal = (checkoutUrl, buttonText, defaultPain) => {
    setModalConfig({
      isOpen: true,
      checkoutUrl,
      buttonText,
      defaultPain
    });
  };

  const handleModalClose = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
    // Refresh leads count on modal close
    const storedLeads = JSON.parse(localStorage.getItem("vph_captured_leads") || "[]");
    setTotalLeads(storedLeads.length);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Sonner Toaster component for notification flows */}
      <Toaster position="top-right" richColors />

      {/* Persistent Page Switcher and Header navigation */}
      <PageSwitcher
        activePage={activePage}
        onPageChange={changePage}
        totalLeads={totalLeads}
      />

      {/* Active Strategy Content Container */}
      <main className="flex-grow">
        {activePage === "resume-pain" && (
          <ResumePainPage onOpenModal={triggerModal} />
        )}
        
        {activePage === "interview-fear" && (
          <InterviewFearPage onOpenModal={triggerModal} />
        )}

        {activePage === "experience-gap" && (
          <ExperienceGapPage onOpenModal={triggerModal} />
        )}

        {activePage === "career-confusion" && (
          <CareerConfusionPage onOpenModal={triggerModal} />
        )}

        {activePage === "admin-leads" && (
          <SubmissionsDashboard />
        )}
      </main>

      {/* Lead Capture Modal Popup */}
      <LeadCaptureModal
        isOpen={modalConfig.isOpen}
        onClose={handleModalClose}
        defaultPain={modalConfig.defaultPain}
        checkoutUrl={modalConfig.checkoutUrl}
        buttonText={modalConfig.buttonText}
      />

      {/* Global Footer (All Pages) */}
      <footer className="bg-slate-950 text-[#FAFAFA] border-t border-slate-900 py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8">
          
          <div className="md:col-span-4 space-y-4">
            <span className="text-xl font-black uppercase text-[#FAFAFA] tracking-tight">Vedula Progress Hub</span>
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
              Accelerating technical career alignments through action-backed, high-contrast diagnostics. Built inside Mango Studio.
            </p>
          </div>

          <div className="md:col-span-4 space-y-3">
            <span className="text-xs font-black uppercase tracking-wider text-[#0066FF] block">Our Portals</span>
            <div className="space-y-1.5 text-xs sm:text-sm">
              <a 
                href="https://learn.vedulaconsulting.com" 
                target="_blank" 
                rel="noreferrer"
                className="text-slate-400 hover:text-white flex items-center gap-1"
              >
                learn.vedulaconsulting.com <ArrowUpRight className="h-3 w-3" />
              </a>
              <a 
                href="https://vedulaprogress.lovable.app" 
                target="_blank" 
                rel="noreferrer"
                className="text-slate-400 hover:text-white flex items-center gap-1"
              >
                vedulaprogress.lovable.app <ArrowUpRight className="h-3 w-3" />
              </a>
            </div>
          </div>

          <div className="md:col-span-4 space-y-3">
            <span className="text-xs font-black uppercase tracking-wider text-[#0066FF] block">Connect / Social Media</span>
            <div className="flex gap-4">
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="p-2.5 bg-slate-900 hover:bg-[#0066FF] text-white rounded-xl transition-all shadow-md">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="p-2.5 bg-slate-900 hover:bg-pink-600 text-white rounded-xl transition-all shadow-md">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noreferrer" className="p-2.5 bg-slate-900 hover:bg-rose-600 text-white rounded-xl transition-all shadow-md">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

        </div>

        <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-slate-900 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-500 gap-4">
          <span>© 2026 Vedula Progress Hub. All rights reserved.</span>
          <div className="flex gap-4">
            <a href="#privacy" className="hover:underline">Privacy Policy</a>
            <a href="#disclaimer" className="hover:underline">Disclaimer</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
